import { atom } from "jotai";
import { atomWithHash, splitAtom } from "jotai/utils";
import type { ExtraFieldType } from "./types";
import * as D from "date-fns";
import { formatISO, parseISO } from "date-fns";
import _, { clamp } from "lodash";
import { step1_predefinedgoals, step3_predefinedxpenses } from "./strings";
import { mapRange } from "./mapRange";
import { steps } from "./useNav";

export const StepAtom = atomWithHash("StepAtom", steps.Step1);

export const IsPrintingAtom = atom(false);
export const ProjectNameAtom = atomWithHash("ProjectNameAtom", "", {
  replaceState: true
});
export const StartDateAtom = atomWithHash<Date | null>("StartDateAtom", null, {
  serialize(x) {
    return x ? formatISO(x) : null;
  },
  deserialize(x) {
    return x && x !== "null" ? parseISO(x) : null;
  },
  replaceState: true
});
export const EndDateAtom = atomWithHash<Date | undefined>("EndDateAtom", null, {
  serialize(x) {
    return x ? formatISO(x) : null;
  },
  deserialize(x) {
    return x && x !== "null" ? parseISO(x) : null;
  },
  replaceState: true
});
export const MembersIncreaseAtom = atomWithHash("MembersIncreaseAtom", 0, {
  replaceState: true
});
export const FeeIncreaseAtom = atomWithHash("FeeIncreaseAtom", 0, {
  replaceState: true
});
export const MembersAtom = atomWithHash("MembersAtom", 0, {
  replaceState: true
});
export const _FeeAtom = atomWithHash("FeeAtom", 0, { replaceState: true });
export const FeeAtom = atom(get => get(_FeeAtom) * 12);
export const YearsBackingAtom = atomWithHash("YearsAtom", null, {
  replaceState: true
});
export const YearsAtom = atom(
  get =>
    get(YearsBackingAtom) ??
    clamp(
      Math.round(
        D.differenceInMonths(get(EndDateAtom), get(StartDateAtom)) / 12
      ),
      1,
      12
    ),
  (get, set, update) => set(YearsBackingAtom, update)
);

export const MembersNewAtom = atom(
  get => get(MembersAtom) * (1 + get(MembersIncreaseAtom))
);
export const FeeNewAtom = atom(
  get => get(FeeAtom) * (1 + get(FeeIncreaseAtom))
);
export const CustomIncomeAtom = atom(get =>
  get(ExtraIncomesAtom).reduce(
    (previousValue, currentValue) => previousValue + currentValue.value,
    0
  )
);
export const IncomeAtom = atom(
  get => get(MembersAtom) * get(FeeAtom) + get(CustomIncomeAtom)
);
export const InitialAtom = atom(get => get(IncomeAtom));

export const ExtraGoalsAtom = atomWithHash<ExtraFieldType[]>(
  "ExtraGoalsAtom",
  [],
  { replaceState: true }
);
export const ExtraGoalAtomsAtom = splitAtom(ExtraGoalsAtom);

export const PredefinedGoalsAtom = atom(get =>
  step1_predefinedgoals.reduce(
    (previousValue, currentValue) => previousValue + get(currentValue.atom),
    0
  )
);

export const CustomGoalsAtom = atom(get =>
  get(ExtraGoalsAtom).reduce(
    (previousValue, currentValue) => previousValue + currentValue.value,
    0
  )
);

export const GoalAtom = atom(
  get => get(PredefinedGoalsAtom) + get(CustomGoalsAtom)
);
export const ExtraIncomesAtom = atomWithHash<ExtraFieldType[]>(
  "ExtraIncomesAtom",
  [],
  { replaceState: true }
);
export const ExtraIncomeAtomsAtom = splitAtom(ExtraIncomesAtom);

export const ExtraExpensesAtom = atomWithHash<ExtraFieldType[]>(
  "ExtraExpensesAtom",
  [],
  { replaceState: true }
);
export const ExtraExpenseAtomsAtom = splitAtom(ExtraExpensesAtom);

export const PredefinedExpensesAtom = atom(get =>
  step3_predefinedxpenses.reduce(
    (previousValue, currentValue) => previousValue + get(currentValue.atom),
    0
  )
);
export const CustomExpensesAtom = atom(get =>
  get(ExtraExpensesAtom).reduce(
    (previousValue, currentValue) => previousValue + currentValue.value,
    0
  )
);

export const ExpensesAtom = atom(
  get => get(PredefinedExpensesAtom) + get(CustomExpensesAtom)
);
export interface TableRow {
  date: string;
  membersTotal: number;
  membersNew: number;
  fee: number;
}
export const TableAtom = atom<TableRow[]>(get => {
  return _.range(0, get(YearsAtom), 0.5).map(x => {
    let date1 = get(StartDateAtom);
    let date = date1 ? D.addMonths(date1, x * 12) : null;
    const years = get(YearsAtom);
    const members = get(MembersAtom);
    const dm = get(MembersIncreaseAtom);
    const getMembersTotal = (x: number) => {
      let dt = years;
      let mi = members;
      return mapRange([0, dt], [mi, mi + mi * dm], x);
    };
    const fee = get(FeeAtom);
    const df = get(FeeIncreaseAtom);
    let dt = years;
    let fi = fee;
    const getFee = (x: number) => {
      let ndf = mapRange([0, dt + 1], [0, df], x + 1);
      return fi + fi * ndf;
    };
    return {
      date: `${D.format(date, "MMMM")} ${D.format(date, "yyyy")}`,
      membersTotal: getMembersTotal(x),
      membersNew: getMembersTotal(x) - get(MembersAtom),
      fee: getFee(x) / fee - 1
    };
  });
});
